import { defineComponent, h, provide, ref, watch } from 'vue';
import { Modal } from '@/ui/components/Modal';
import { VNodeData } from 'vue/types/vnode.d';
import { emptyChecklistTemplate, ChecklistTemplateEditable } from '@/ui/DTO';
import { loader, pageSubtitle } from '@/ui/components';
import { t } from '@/ui/i18n/i18n';
import { SelectOptions } from '@/ui/types';
import { useTask } from '@/ui/concerns';
import {
  filterListToOptions,
  sequence,
  filterListToEditableGroups,
  filterListToEditableUsers,
} from '@/ui/helpers';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as getAllGroups } from '@/services/api/resources/group';
import { filter as getAllChecklists } from '@/services/api/resources/checklist';
import { Button } from '@/ui/components/Button';
import cloneDeep from 'lodash/cloneDeep';
import { CREATE_TEMPLATE_MODAL_WIDTH } from '@/ui/components/modals/constants';
import ChecklistTemplateForm from '@/ui/components/checklistTemplate/checklistCreateTemplateModal/ChecklistTemplateForm.vue';
import { useChecklistTemplate } from '@/ui/components/checklistTemplate/useChecklistTemplate';
import { createUserIdentifierFromActionPageKey } from '@/ui/injectionKeys';
import { CreateUserModelIdentifier } from '@/ui/components/user/CreateUserModal';
import { useAssignees } from '@/ui/concerns/useAssignees';

interface ChecklistTemplateCreateModalProps {
  active: boolean;
  checklistTemplate?: ChecklistTemplateEditable | null;
}

export const ChecklistTemplateCreateModal = defineComponent<ChecklistTemplateCreateModalProps>({
  name: 'ChecklistTemplateCreateModal',
  props: ['active', 'checklistTemplate'],
  setup(props, context) {
    provide(createUserIdentifierFromActionPageKey, CreateUserModelIdentifier.actionCreator);

    const checklists = ref<SelectOptions>([]);
    const checklistTemplateEditable = ref(props.checklistTemplate ? cloneDeep(props.checklistTemplate) : emptyChecklistTemplate());
    const {
      validationErrors,
      hasNoValidationErrors,
      showValidationErrors,
    } = useChecklistTemplate(checklistTemplateEditable);

    const closeModal = () => {
      checklistTemplateEditable.value = emptyChecklistTemplate();
      context.emit('close');
    };

    const onSaveClick = () => {
      showValidationErrors.value = true;

      if (hasNoValidationErrors.value) {
        checklistTemplateEditable.value.assignees = filterListToEditableUsers(checklistTemplateEditable.value.assignees);
        checklistTemplateEditable.value.assignedGroups = filterListToEditableGroups(checklistTemplateEditable.value.assignedGroups);

        context.emit('save', cloneDeep(checklistTemplateEditable.value));

        checklistTemplateEditable.value = emptyChecklistTemplate();
        showValidationErrors.value = false;
      }
    };

    const {
      assigneeOptions,
      fillGroupsOptions,
      fillAssigneeOptions,
      getUsers,
    } = useAssignees();

    const getFilters = useTask(
      () => sequence(getAllUsers(), getAllGroups(), getAllChecklists()),
      ([users, groups, checklistList]) => {
        fillGroupsOptions(groups);
        fillAssigneeOptions(users);
        checklists.value = filterListToOptions(checklistList);
      },
    );

    const onChecklistTemplatePropChange = (resource: ChecklistTemplateEditable | null | undefined) => {
      checklistTemplateEditable.value = cloneDeep(resource) || emptyChecklistTemplate();
    };

    watch(() => props.checklistTemplate, onChecklistTemplatePropChange);

    return function render() {
      return h(Modal, {
        props: {
          ...props,
          width: CREATE_TEMPLATE_MODAL_WIDTH,
          ariaRole: 'dialog',
          trapFocus: true,
          destroyOnHide: false,
          ariaModal: true,
        },
        on: {
          close: closeModal,
        },
        scopedSlots: {
          actions: () => h('div', [
            h(Button, {
              props: { type: 'is-text', btnClass: 'mr-2 has-text-weight-semibold' },
              on: { click: closeModal },
            }, t('modal.cancel')),
            h(Button, {
              props: {
                loading: false,
                disabled: false,
              },
              on: { click: onSaveClick },
            }, t('common.save')),
          ]),
        },
      }, [
        loader('div', getFilters, [
          pageSubtitle(t('checklist-template.inspection')),
          h(ChecklistTemplateForm, {
            props: {
              record: checklistTemplateEditable.value,
              assigneeOptions: assigneeOptions.value,
              checklists: checklists.value,
              validationErrors: validationErrors.value,
            },
            on: {
              'update-user-options': () => getUsers.run(),
            },
          }),
        ]),
      ]);
    };
  },
});

export function checklistTemplateCreateModal(data: VNodeData & { props: ChecklistTemplateCreateModalProps }) {
  return h(ChecklistTemplateCreateModal, data);
}
